import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type AuthInitStep =
  | "idle"
  | "subscribing"
  | "subscribed"
  | "getSession:pending"
  | "getSession:resolved"
  | "getSession:error"
  | "getSession:timeout"
  | "retry"
  | "onAuthStateChange";

export type AuthDebugInfo = {
  steps: { step: AuthInitStep; at: number; detail?: string }[];
  startedAt: number;
  lastEvent: string | null;
  lastError: string | null;
  getSessionMs: number | null;
  hasSubscription: boolean;
  supabaseUrl: string | undefined;
  hasPublishableKey: boolean;
};

type AuthCtx = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  initError: string | null;
  retry: () => void;
  signOut: () => Promise<void>;
  debug: AuthDebugInfo;
};

const defaultDebug: AuthDebugInfo = {
  steps: [],
  startedAt: 0,
  lastEvent: null,
  lastError: null,
  getSessionMs: null,
  hasSubscription: false,
  supabaseUrl: undefined,
  hasPublishableKey: false,
};

const Ctx = createContext<AuthCtx>({
  user: null,
  session: null,
  loading: true,
  initError: null,
  retry: () => {},
  signOut: async () => {},
  debug: defaultDebug,
});

const SESSION_INIT_TIMEOUT_MS = 6000;

let lastKnownSession: Session | null = null;

function readPersistedSession(): Session | null {
  if (typeof window === "undefined") return null;
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith("sb-") || !key.endsWith("-auth-token")) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as unknown;
      const candidate =
        parsed && typeof parsed === "object" && "currentSession" in parsed
          ? (parsed as { currentSession?: Partial<Session> }).currentSession
          : (parsed as Partial<Session> | null);
      if (candidate?.access_token && candidate?.user) return candidate as Session;
    }
  } catch {
    /* ignore malformed/blocked storage and fall back to async session init */
  }
  return null;
}

function sameSession(a: Session | null, b: Session | null) {
  return a?.access_token === b?.access_token && a?.user?.id === b?.user?.id;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const initialSession = lastKnownSession ?? readPersistedSession();
  const [session, setSessionState] = useState<Session | null>(initialSession);
  const [loading, setLoading] = useState(!initialSession);
  const [initError, setInitError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [debug, setDebug] = useState<AuthDebugInfo>(() => ({
    ...defaultDebug,
    startedAt: Date.now(),
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL as string | undefined,
    hasPublishableKey: Boolean(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY),
  }));
  const startedAtRef = useRef<number>(Date.now());
  const sessionRef = useRef<Session | null>(initialSession);

  const setSession = useCallback((next: Session | null) => {
    lastKnownSession = next;
    sessionRef.current = next;
    setSessionState((prev) => (sameSession(prev, next) ? prev : next));
  }, []);

  const recordStep = (step: AuthInitStep, detail?: string) => {
    setDebug((d) => ({
      ...d,
      steps: [...d.steps, { step, at: Date.now(), detail }].slice(-20),
    }));
  };

  useEffect(() => {
    let timedOut = false;
    let settled = false;
    setInitError(null);
    setLoading(!sessionRef.current);
    recordStep("subscribing");
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setDebug((d) => ({ ...d, lastEvent: event, hasSubscription: true }));
      recordStep("onAuthStateChange", event);
      settled = true;
      setInitError(null);
      if (event === "SIGNED_OUT") {
        setSession(null);
        setLoading(false);
        return;
      }
      if (s) {
        setSession(s);
      }
      setLoading(false);
    });
    recordStep("subscribed");
    setDebug((d) => ({ ...d, hasSubscription: true }));

    const t0 = Date.now();
    recordStep("getSession:pending");
    const timeoutId = setTimeout(() => {
      if (settled) return;
      timedOut = true;
      const msg = `Session-Initialisierung hat nach ${SESSION_INIT_TIMEOUT_MS}ms nicht geantwortet.`;
      recordStep("getSession:timeout", `${SESSION_INIT_TIMEOUT_MS}ms`);
      setDebug((d) => ({
        ...d,
        lastError: msg,
        getSessionMs: Date.now() - t0,
      }));
      setInitError(msg);
      setLoading(false);
    }, SESSION_INIT_TIMEOUT_MS);

    supabase.auth
      .getSession()
      .then(({ data, error }) => {
        if (timedOut) return;
        settled = true;
        clearTimeout(timeoutId);
        const ms = Date.now() - t0;
        setDebug((d) => ({ ...d, getSessionMs: ms }));
        if (error) {
          recordStep("getSession:error", error.message);
          setDebug((d) => ({ ...d, lastError: error.message }));
          if (!sessionRef.current) setInitError(error.message);
        } else {
          recordStep("getSession:resolved", data.session ? "session" : "no-session");
          setInitError(null);
          setSession(data.session);
        }
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (timedOut) return;
        settled = true;
        clearTimeout(timeoutId);
        const msg = e instanceof Error ? e.message : String(e);
        recordStep("getSession:error", msg);
        setDebug((d) => ({
          ...d,
          lastError: msg,
          getSessionMs: Date.now() - t0,
        }));
        if (!sessionRef.current) setInitError(msg);
        setLoading(false);
      });
    return () => {
      clearTimeout(timeoutId);
      sub.subscription.unsubscribe();
    };
  }, [attempt]);

  const retry = useCallback(() => {
    setDebug((d) => ({
      ...d,
      steps: [...d.steps, { step: "retry" as AuthInitStep, at: Date.now() }].slice(-20),
    }));
    setAttempt((n) => n + 1);
  }, []);

  // Context-Value memoisieren — sonst rendern AppSidebar/AuthLayout/...
  // bei jedem AuthProvider-Render neu, auch wenn sich nichts geändert hat.
  const value = useMemo<AuthCtx>(
    () => ({
      user: session?.user ?? null,
      session,
      loading,
      initError,
      retry,
      signOut: async () => {
        await supabase.auth.signOut();
      },
      debug: { ...debug, startedAt: startedAtRef.current },
    }),
    [session, loading, initError, debug, retry],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
